/**
 * Release notes, shown instantly from the build-time bundle and upgraded in
 * place once the API responds — the same progressive-enhancement shape as
 * {@link useLatestDownload}, and for the same reason: there is always
 * something to render, so the marketing site never depends on the worker
 * being reachable.
 *
 * What the API adds over the bundle: notes edited (or published) since the
 * last portal build. What the bundle adds over the API: an instant first
 * paint, and a page that still works when the worker is down. Neither is
 * redundant, so a fetch failure here is silently absorbed.
 */

import { useEffect, useState } from "react";

import { type Release, type ReleaseSummary, findRelease, sortReleases } from "@bigbear/shared";

import { fetchLatestRelease, fetchRelease, fetchReleaseSummaries } from "./api/releasesApi";
import { latestRelease, releases as bundled } from "./releases";

const bundledSummaries: readonly ReleaseSummary[] = bundled.map(({ version, date, headline }) => ({
  version,
  date,
  headline,
}));

/** Every release, newest first — bundled corpus merged with whatever the API knows. */
export function useReleases(): readonly ReleaseSummary[] {
  const [summaries, setSummaries] = useState<readonly ReleaseSummary[]>(bundledSummaries);

  useEffect(() => {
    const controller = new AbortController();

    fetchReleaseSummaries(controller.signal)
      .then((fromApi) => {
        if (controller.signal.aborted) return;
        setSummaries(mergeByVersion(bundledSummaries, fromApi));
      })
      .catch(() => {
        // The bundle is already showing; a missing upgrade is not a failure
        // worth putting on screen.
      });

    return () => controller.abort();
  }, []);

  return summaries;
}

export interface ReleaseLookup {
  readonly release: Release | null;
  /**
   * False only while the API is still deciding about a version the bundle
   * doesn't have — the one case where "not found" would be premature.
   */
  readonly settled: boolean;
}

function seedFor(version: string): ReleaseLookup {
  const fromBundle = findRelease(bundled, version) ?? null;
  return { release: fromBundle, settled: fromBundle !== null };
}

/** One release with its body, seeded from the bundle when it's known there. */
export function useRelease(version: string): ReleaseLookup {
  const [lookup, setLookup] = useState<ReleaseLookup>(() => seedFor(version));
  const [lookedUp, setLookedUp] = useState(version);

  // Reset during render, not in the effect below. `useState`'s initial value
  // is only ever used on the first render of a component instance, so when
  // `version` changes on a later render — a client-side navigation from one
  // release to another reuses the same instance — state seeded for the old
  // version would otherwise still be what this render returns. Effects run
  // after the browser paints, so that stale answer reaches the screen: the
  // previous release's notes under the new release's heading. Re-setting here
  // makes React discard this render and immediately re-run with the right
  // seed, before anything is painted.
  if (lookedUp !== version) {
    setLookedUp(version);
    setLookup(seedFor(version));
  }

  useEffect(() => {
    const controller = new AbortController();

    fetchRelease(version, controller.signal)
      .then((fromApi) => {
        if (controller.signal.aborted) return;
        setLookup((current) => (fromApi ? { release: fromApi, settled: true } : { ...current, settled: true }));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        // Unreachable API: keep whatever the bundle had, and stop waiting — a
        // permanent spinner would be worse than an honest 404.
        setLookup((current) => ({ ...current, settled: true }));
      });

    return () => controller.abort();
  }, [version]);

  return lookup;
}

/**
 * The newest release with its body, in one API answer.
 *
 * Deliberately not `useRelease(releases[0].version)`: deriving the version
 * from the list and then fetching it is two responses that can reflect two
 * different moments, so the heading and the notes under it can disagree.
 */
export function useLatestRelease(): ReleaseLookup {
  const [lookup, setLookup] = useState<ReleaseLookup>(() => ({
    release: latestRelease ?? null,
    settled: latestRelease !== undefined,
  }));

  useEffect(() => {
    const controller = new AbortController();

    fetchLatestRelease(controller.signal)
      .then((fromApi) => {
        if (controller.signal.aborted) return;
        setLookup((current) => (fromApi ? { release: fromApi, settled: true } : { ...current, settled: true }));
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setLookup((current) => ({ ...current, settled: true }));
      });

    return () => controller.abort();
  }, []);

  return lookup;
}

/** Union of both sources, newest first, with the API's copy winning on conflict. */
function mergeByVersion(
  bundledList: readonly ReleaseSummary[],
  fromApi: readonly ReleaseSummary[],
): readonly ReleaseSummary[] {
  const byVersion = new Map<string, ReleaseSummary>();
  for (const summary of bundledList) byVersion.set(summary.version, summary);
  for (const summary of fromApi) byVersion.set(summary.version, summary);
  return sortReleases([...byVersion.values()]);
}
